import { useCallback, useEffect, useRef, useState } from 'react';

interface Options<T> {
  enabled?: boolean;
  intervalMs: number;
  fetcher: (signal: AbortSignal) => Promise<T>;
  onUnauthenticated: () => void;
  resetKey?: string;
}

export function useLivePoll<T>({
  enabled = true,
  intervalMs,
  fetcher,
  onUnauthenticated,
  resetKey = '',
}: Options<T>) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const fetcherRef = useRef(fetcher);
  const dataRef = useRef<T | null>(null);
  fetcherRef.current = fetcher;

  const run = useCallback(
    async (isRefresh = false) => {
      if (typeof document !== 'undefined' && document.hidden) return;
      if (isRefresh || dataRef.current) setRefreshing(true);
      else setLoading(true);
      setError('');
      const controller = new AbortController();
      try {
        const next = await fetcherRef.current(controller.signal);
        dataRef.current = next;
        setData(next);
      } catch (err) {
        const code = (err as Error & { code?: string }).code;
        if (code === 'UNAUTHENTICATED') {
          onUnauthenticated();
          return;
        }
        if ((err as Error).name === 'AbortError') return;
        setError((err as Error).message || 'Failed to load');
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [onUnauthenticated],
  );

  useEffect(() => {
    if (!enabled) return undefined;
    void run(false);
    const timer = window.setInterval(() => {
      void run(true);
    }, intervalMs);
    const onVis = () => {
      if (!document.hidden) void run(true);
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [enabled, intervalMs, run, resetKey]);

  return { data, loading, refreshing, error, reload: run };
}
